import React, { FC, useEffect, useMemo } from 'react'

import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import { geoLandingDataAction } from 'actions/landing/geoLandingDataAction'
import { clearLandingDataAction } from 'actions/landing/landingAction'
import { setLgbtBlockedAction } from 'actions/landing/seoAction'
import { fetchCurrentLocationAction } from 'actions/location/fetchCurrentLocationAction'
import { setLandingPageAction } from 'actions/system/systemAction'
import { geoLandingOperation } from 'api/clientStatisticApi'
import { DEFAULT_LOCALE } from 'common/constants'
import { LandingBreadCrumbs } from 'components/block/Seo/BreadCrumbs/LandingBreadCrumbs'
import { createLocationParams } from 'components/page/Landing/Landing.functions'
import { createLocationName } from 'components/page/Landing/LandingView/createLocationName'
import { LandingFilter } from 'components/page/Landing/LandingView/LandingFilter/LandingFilter'
import {
  LandingJoinBanner,
  LandingBottomBanner,
} from 'components/page/Landing/LandingView/LandingJoinBanner'
import { LandingTitleSwitch } from 'components/page/Landing/LandingView/LandingTitleSwitch'
import { LandingInnerContent } from 'components/page/Landing/LandingView/LandingView.styled'
import { TopLocationLinksList } from 'components/page/Landing/LandingView/TopLocationLinksList'
import { useLandingParams } from 'components/page/Landing/useLandingParams'
import { searchPath } from 'components/page/Search/Search.paths'
import { indexPath, notFoundPath } from 'components/paths'
import { DefaultLoader } from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { SpaceSeparatorNext } from 'components/presentational/separator'
import RedirectWithStatus from 'components/system/RedirectWithStatus'
import { PerformanceEnd } from 'components/system/statistic/PerfomanceEnd'
import { PerformanceStart } from 'components/system/statistic/PerfomanceStart'
import { isRussia } from 'functions/country/country.functions'
import { usePrevious } from 'hooks/usePrevious'
import { useReducersInsert } from 'hooks/useReducersInsert'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'
import { landingReducer } from 'reducers/landing/landingReducer'
import { mainSearchReducer } from 'reducers/search/mainSearchReducer'

import { SecondarySnippet } from './SecondarySnippet'
import { UsersGrid } from './UsersGrid'
import { GAYS, genderAlternative, genderList, LESBIANS } from '../constants'
import LandingLayout from '../LandingLayout/LandingLayout'
import { LandingSeoText } from '../LandingSeoText/LandingSeoText'
import { isSeriousRelaionship } from '../Special/Special.functions'

const removeLgbt = new RegExp(`\/(${GAYS}|${LESBIANS})`)

const LandingViewIndex: FC = () => {
  useReducersInsert({ mainSearchReducer, landingReducer })
  const intl = useIntl()
  const dispatch = useDispatch()
  const {
    usersLoading,
    userLoadingFailed,
    formSaved,
    locale,
    pathname,
    authorized,
    header: metaDataHeader,
    seoSnippetTitle,
    seoLocation,
    topLocationLinks,
    seoLocationResolved,
    lgbtBlocked,
    currentLocation,
    currentLocationServerErrorLoaded,
  } = useShallowEqualSelector(
    ({
      mainSearchReducer: { formSaved },
      systemReducer: { locale },
      router: {
        location: { pathname },
      },
      authorizationReducer: { authorized },
      landingReducer: {
        usersLoading,
        userLoadingFailed,
        seoSnippetTitle,
        seoLocation,
        topLocationLinks,
        seoLocationResolved,
        lgbtBlocked,
      },
      location: { currentLocation, currentLocationServerErrorLoaded },
      metaData: { header },
    }) => ({
      usersLoading,
      userLoadingFailed,
      formSaved,
      locale,
      pathname,
      authorized,
      header,
      seoSnippetTitle,
      seoLocation,
      topLocationLinks,
      seoLocationResolved,
      lgbtBlocked,
      currentLocation,
      currentLocationServerErrorLoaded,
    })
  )

  const params = useLandingParams()

  const { locationId, locationType } = useMemo(
    () => createLocationParams(params),
    [params]
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(clearLandingDataAction())
        await dispatch(geoLandingDataAction(params, intl))
        dispatch(setLandingPageAction())
      } catch (error) {
        console.error(error)
      }
    }
    fetchData().catch(console.error)
  }, [dispatch, intl, locationId, locationType, params])

  const previousFormSaved = usePrevious(formSaved)

  useEffect(() => {
    // https://redmine.mamba.ru/issues/100353
    if (previousFormSaved !== formSaved) {
      window.location.href = window.location.origin + searchPath
    }
  }, [formSaved, previousFormSaved])

  useEffect(() => {
    if (currentLocation === null && !currentLocationServerErrorLoaded) {
      dispatch(fetchCurrentLocationAction())
    } else {
      dispatch(setLgbtBlockedAction(isRussia(currentLocation?.country!)))
    }
  }, [currentLocation, currentLocationServerErrorLoaded, dispatch])

  const { country, countryId } = params
  const isLocationInRussia = isRussia(Number(countryId)) || country === 'russia'

  /**
   * Геев сразу нафиг:
   * https://youtrack.mamba.ru/issue/M-4961
   *
   * Сделал грубо заменой, но для этой задачи должно хватить без последствий.
   *
   * 1. Для России должно быть закрыто через бек `userLoadingFailed`.
   * Россияне не могут видеть никакие гейские геолендинги.
   * 2. Никто не может видеть российские гейские геолендинги
   * 3. Условные европейцы могут видеть европейские гейские геолендинги
   *
   * https://youtrack.mamba.ru/issue/M-5106#focus=Comments-4-32980.0-0
   */
  if (
    (userLoadingFailed || isLocationInRussia || lgbtBlocked) &&
    [GAYS, LESBIANS].includes(params.gender!)
  ) {
    return (
      <RedirectWithStatus
        to={pathname.replace(removeLgbt, '')}
        status={301}
        uid="landing redirect up"
      />
    )
  }

  if (userLoadingFailed) {
    /** Скорее всего скрыто от РКН: https://youtrack.mamba.ru/issue/M-4377 */
    return (
      <RedirectWithStatus
        to={notFoundPath}
        status={302}
        uid="landing force redirect"
      />
    )
  }

  if (seoLocation === null && seoLocationResolved) {
    console.error(`Empty location ${pathname}, redirect to index`)
    return (
      <RedirectWithStatus to={indexPath} status={302} uid="LandingViewIndex" />
    )
  }

  if (usersLoading || !seoLocationResolved || !seoLocation) {
    return (
      <LandingLayout>
        <PerformanceStart operation={geoLandingOperation} />
        <LandingInnerContent>
          <DefaultLoader />
        </LandingInnerContent>
      </LandingLayout>
    )
  }

  const locationName = createLocationName(seoLocation.location)

  return (
    <LandingLayout>
      <LandingInnerContent>
        {seoLocation &&
          seoLocation.ancestorsLinks &&
          seoLocation.ancestorsLinks.length > 0 && (
            <LandingBreadCrumbs gender={params.gender} />
          )}
        <LandingTitleSwitch
          gender={params.gender}
          locationName={locationName}
          title={metaDataHeader ?? seoSnippetTitle}
        />
        <LandingFilter genderList={genderList} />
        {locale === DEFAULT_LOCALE && <LandingSeoText />}
        <UsersGrid />
        <SpaceSeparatorNext height="20px" />
        {!(lgbtBlocked || isLocationInRussia) && (
          <LandingFilter genderList={genderAlternative} />
        )}
        {!authorized && <LandingBottomBanner />}
        <SecondarySnippet />
        {!authorized && <LandingJoinBanner />}
        {!isSeriousRelaionship(pathname) &&
          Boolean(topLocationLinks.length) && (
            <TopLocationLinksList locationName={locationName} />
          )}
      </LandingInnerContent>
      <PerformanceEnd operation={geoLandingOperation} />
    </LandingLayout>
  )
}

export default LandingViewIndex
